import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUnlock,
  faBoxOpen,
  faCompass,
  faArrowRotateLeft,
} from '@fortawesome/free-solid-svg-icons';
import Modal from './Modal';
import useRedesignExperiment from '../hooks/useRedesignExperiment';
import {
  BUCKET,
  ROLLOUT_ARM,
  VERSION,
  FORCE_OLD_DESIGN,
  hasDismissedInviteModal,
  dismissInviteModal,
  setActiveVersion,
} from '../services/redesignExperiment';

// Pages where a promo modal would get in the way of the reason they came.
const SUPPRESSED_PATHS = ['/login', '/forgot-password', '/admin/access', '/magiclink/redirect', '/gift/redeem'];
// Let the page settle (and any deep-link / payment modals claim the screen)
// before we consider opening.
const OPEN_DELAY_MS = 1500;
// Dev-only: ?redesign_invite=preview forces the modal open for anyone, ignores
// the dismissal cache, and makes "Switch" a local-only switch on this device
// (nothing written to the API or Klaviyo).
const isPreview = () =>
  process.env.NODE_ENV !== 'production' &&
  new URLSearchParams(window.location.search).get('redesign_invite') === 'preview';

const PERKS = [
  { icon: faCompass, text: 'Cleaner, simpler navigation' },
  { icon: faBoxOpen, text: 'Edit your box and add-ons in fewer taps' },
  { icon: faArrowRotateLeft, text: 'Switch back anytime from the menu' },
];

/**
 * One-time invitation for existing members in the rollout's 'invite' arm who
 * are still on the old design — a more prominent way in than the sidebar
 * switcher. Closing it (any way) or switching caches that on this device so it
 * never reshows. Test-bucket members (the new-member A/B) never see it: pushing
 * their old arm across would contaminate that test.
 */
function InviteModal({ blocked }) {
  const location = useLocation();
  const { bucket, rolloutArm, activeVersion, assignmentSettled, switchVersion } = useRedesignExperiment();
  const [open, setOpen] = useState(false);
  // Once closed, never reopen within this page visit (preview mode skips the
  // persisted dismissal, so without this it popped straight back up).
  const [closed, setClosed] = useState(false);

  const params = new URLSearchParams(location.search);
  const preview = isPreview();
  const eligible = preview || (
    !FORCE_OLD_DESIGN &&
    assignmentSettled &&
    bucket === BUCKET.CONTROL &&
    rolloutArm === ROLLOUT_ARM.INVITE &&
    activeVersion !== VERSION.NEW &&
    !hasDismissedInviteModal() &&
    // Deep links (magic-link product popups, ?design= switches) take priority.
    !params.has('product') &&
    !params.has('design') &&
    !params.has('modal'));

  useEffect(() => {
    if (!eligible || blocked || open || closed) return undefined;
    const t = setTimeout(() => {
      // Don't stack on top of another dialog that opened in the meantime.
      if (document.querySelector('[role="dialog"]')) return;
      setOpen(true);
    }, OPEN_DELAY_MS);
    return () => clearTimeout(t);
  }, [eligible, blocked, open, closed]);

  const close = useCallback(() => {
    if (!preview) dismissInviteModal();
    setClosed(true);
    setOpen(false);
  }, [preview]);

  const handleSwitch = () => {
    if (preview) {
      // Local-only switch (no email → no API / Klaviyo writes); the next
      // assignment read on reload puts the device back where the server says.
      setActiveVersion(null, VERSION.NEW);
    } else {
      dismissInviteModal();
      switchVersion(VERSION.NEW, 'modal');
    }
    setClosed(true);
    setOpen(false);
  };

  return (
    <Modal name="redesign-invite" isOpen={open} onClose={close} closeOnBackdropClick size="md" contentPadding="p-6 sm:p-8">
      <div className="text-center">
        <img
          src="/wildgrain_logo_192.png"
          alt="Wildgrain"
          className="mx-auto mb-4 h-14 w-14 rounded-full shadow-md"
        />
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-primary">You’re invited</p>
        <h2 className="mb-2 font-idler text-2xl leading-tight text-[#544947]">Try the new member portal</h2>
        <p className="mx-auto mb-5 max-w-sm text-sm leading-snug text-gray-600">
          We’ve redesigned your portal to make managing your Wildgrain membership quicker and easier.
        </p>
      </div>

      <ul className="mb-6 space-y-2.5">
        {PERKS.map(({ icon, text }) => (
          <li key={text} className="flex items-center justify-center gap-3 text-center text-sm text-gray-700">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-light_red text-primary">
              <FontAwesomeIcon icon={icon} className="h-3.5 w-3.5" />
            </span>
            {text}
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={handleSwitch}
        className="group relative mx-auto flex w-full max-w-xs items-center justify-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-primary to-[#ff8a75] hover:bg-gradient-to-r hover:from-[#f2553b] hover:to-[#ff7a62] py-2.5 text-sm font-semibold normal-case text-white shadow-[0_10px_24px_-8px_rgba(255,98,71,0.6)] ring-1 ring-inset ring-white/20 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_30px_-8px_rgba(255,98,71,0.7)] active:translate-y-0 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
      >
        {/* The global `button:hover { background: none }` base style would
            blank the gradient, so the hover: gradient classes re-assert it. */}
        {/* Soft sheen that sweeps across on hover */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 -skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-[300%] motion-reduce:hidden"
        />
        <FontAwesomeIcon icon={faUnlock} className="h-3.5 w-3.5" />
        Switch to the new design
      </button>
      <button
        type="button"
        onClick={close}
        className="mt-3 w-full py-2 text-sm font-medium normal-case text-gray-500 underline-offset-2 hover:text-gray-700 hover:underline"
      >
        Maybe later
      </button>
    </Modal>
  );
}

// Outer gate: re-checks on every navigation and only mounts the member lookup
// once there's a session, so login pages don't fire a doomed customer fetch.
function RedesignInviteModal({ blocked = false }) {
  const { pathname } = useLocation();
  if (!isPreview() && (FORCE_OLD_DESIGN || hasDismissedInviteModal())) return null;
  if (SUPPRESSED_PATHS.some((p) => pathname.startsWith(p))) return null;
  let hasSession = false;
  try {
    hasSession = localStorage.getItem('rechargeSession') !== null;
  } catch {
    /* storage unavailable */
  }
  if (!hasSession) return null;
  return <InviteModal blocked={blocked} />;
}

export default RedesignInviteModal;
